import { UserRole } from "model/user/userRole";
import React from "react";
import {
    SimpleForm,
    TextInput,
    AutocompleteInput,
    CreateProps,
    Create,
    FormDataConsumer,
    required,
    useNotify,
    useRedirect,
    email,
    Loading,
    useMutation, ReferenceInput, SelectInput, usePermissions, useGetIdentity,
} from "react-admin";
import { AdminResource } from "../resource";
import StandardApiError from "shared/errors/standardApiError";
import { useCountries, useLanguages, useTimezones } from "api/enums";
import { useTranslation } from "react-i18next";
import { Capability } from "reactAdmin/capabilities";

export const UserCreate = (props: CreateProps) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const redirect = useRedirect();
  const timezonesResult = useTimezones();
  const languagesResult = useLanguages();
  const countryResult = useCountries();
  const { permissions: isAuthorized } = usePermissions();
  const { loading: identityLoading, identity } = useGetIdentity();

  const [mutate] = useMutation();
  const save = React.useCallback(
    async (values, redirectTo) => {
      try {
        const res = await mutate(
          {
            type: "create",
            resource: AdminResource.users,
            payload: { data: values },
          },
          { returnPromise: true }
        );
        notify(`User has been invited`);
        redirect(redirectTo, `/${AdminResource.users}`, res.data.id);
      } catch (error) {
          if (error instanceof StandardApiError) {
              return error.validationErrors;
          }
      }
    },
    [mutate, notify, redirect]
  );

  if (timezonesResult.isLoading || languagesResult.isLoading || identityLoading) {
    return <Loading />;
  }

  const filters: Record<string, string|number|boolean> = {
      isActive: true
  };

  if (!isAuthorized || !isAuthorized(Capability.ASSIGN_ANY_CLUB)) {
    filters.id = identity?.organizationId;
  }

  const formValidator = (values: any) => {
    const errors: Record<string, string> = {};
    if (values.role === UserRole.clubAdmin) {
      if (!values.organizationId) {
        errors.organizationId = 'ra.validation.required'
      }
    }
    return errors;
  }

  return (
    <Create {...props}>
      <SimpleForm save={save} validate={formValidator}>
        <TextInput source="emailAddress" validate={[required(), email()]} />
        <TextInput source="firstName" validate={[required()]} />
        <TextInput source="lastName" validate={[required()]} />
        <AutocompleteInput
          source="role"
          choices={Object.values(UserRole).map((item) => ({
            id: item,
            name: item,
          }))}
          validate={[required()]}
        />
        <AutocompleteInput
          source="countryId"
          allowEmpty={true}
          choices={(countryResult.data || []).map((item) => ({
            id: item.id,
            name: item.name,
          }))}
        />
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return (
              <ReferenceInput
                label={t('resource.organization')}
                source="organizationId"
                reference={AdminResource.organizations}
                allowEmpty={formData.role !== UserRole.clubAdmin}
                isRequired={formData.role === UserRole.clubAdmin}
                filter={filters}
              >
                <SelectInput optionText="name" />
              </ReferenceInput>
            );
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  );
};
